import type { MutationTree } from "vuex";
import type {
  SelfDataState,
  Building,
  DownloadConfig,
  DownloadRequest,
} from "@/types";
import {
  LOAD_BUILDINGS,
  LOAD_BUILDINGS_SUCCESS,
  LOAD_BUILDINGS_FAILURE,
  LOAD_VARIABLES,
  LOAD_VARIABLES_SUCCESS,
  LOAD_VARIABLES_FAILURE,
  LOAD_VARIABLES_EXPORT,
  LOAD_VARIABLES_EXPORT_SUCCESS,
  LOAD_VARIABLES_EXPORT_FAILURE,
  LOAD_IMPORTS,
  LOAD_IMPORTS_SUCCESS,
  LOAD_IMPORTS_FAILURE,
  EDIT_IMPORTS,
  EDIT_IMPORTS_SUCCESS,
  EDIT_IMPORTS_FAILURE,
  LOAD_DOWNLOAD_CONFIGS,
  LOAD_DOWNLOAD_CONFIGS_SUCCESS,
  LOAD_DOWNLOAD_CONFIGS_FAILURE,
  LOAD_FILES,
  LOAD_FILES_SUCCESS,
  LOAD_FILES_FAILURE,
  DELETE_FILES,
  DELETE_FILES_SUCCESS,
  DELETE_FILES_FAILURE,
  SEND_DOWNLOAD_REQUEST,
  SEND_DOWNLOAD_REQUEST_SUCCESS,
  SEND_DOWNLOAD_REQUEST_FAILURE,
  SEND_DOWNLOAD_CONFIGS,
  SEND_DOWNLOAD_CONFIGS_SUCCESS,
  SEND_DOWNLOAD_CONFIGS_FAILURE,
  DELETE_DOWNLOAD_CONFIGS,
  DELETE_DOWNLOAD_CONFIGS_SUCCESS,
  DELETE_DOWNLOAD_CONFIGS_FAILURE,
  UPDATE_DOWNLOAD_CONFIGS,
  UPDATE_DOWNLOAD_CONFIGS_SUCCESS,
  UPDATED_DOWNLOAD_CONFIGS_FAILURE,
  CLEAR_DOWNLOAD_CONFIG_SELF_DATE_STORE,
  CLEAR_FILES_SELF_DATA_STORE,
  SAVE_IMPORT_FILE,
  CLEAR_IMPORT_FILE,
  DELETE_IMPORT,
  DELETE_IMPORT_SUCCESS,
  DELETE_IMPORT_FAILURE,
  LOAD_HISTORY_VAR_CHANGE,
  LOAD_LOAD_HISTORY_VAR_CHANGE_SUCCESS,
  LOAD_LOAD_HISTORY_VAR_CHANGE_FAILURE,
} from "./labels";

export const mutations: MutationTree<SelfDataState> = {
  // BUILDINGS
  [LOAD_BUILDINGS](state: SelfDataState) {
    state.buildings = { ...state.buildings, loading: true, error: null };
  },
  [LOAD_BUILDINGS_SUCCESS](state: SelfDataState, buildings: Building[]) {
    state.buildings = { data: buildings, loading: false, error: null };
  },
  [LOAD_BUILDINGS_FAILURE](state: SelfDataState, error: string) {
    state.buildings = { ...state.buildings, loading: false, error: error };
  },
  // VARIABLES
  [LOAD_VARIABLES](state: SelfDataState) {
    state.variables = { ...state.variables, loading: true, error: null };
  },
  [LOAD_VARIABLES_SUCCESS](state: SelfDataState, data: any) {
    state.variables = {
      data: data.variables,
      count: data.count,
      loading: false,
      error: null,
    };
  },
  [LOAD_VARIABLES_FAILURE](state: SelfDataState, error: string) {
    state.variables = { ...state.variables, loading: false, error: error };
  },

  // VARIABLES EXPORT
  [LOAD_VARIABLES_EXPORT](state: SelfDataState) {
    state.variablesExport = {
      ...state.variablesExport,
      loading: true,
      error: null,
    };
  },

  [LOAD_VARIABLES_EXPORT_SUCCESS](state: SelfDataState) {
    state.variablesExport = {
      ...state.variablesExport,
      loading: false,
      error: null,
    };
  },

  [LOAD_VARIABLES_EXPORT_FAILURE](state: SelfDataState, error: string) {
    state.variablesExport = {
      ...state.variablesExport,
      loading: false,
      error: error,
    };
  },

  // IMPORTS
  [LOAD_IMPORTS](state: SelfDataState) {
    state.imports = { ...state.imports, loading: true, error: null };
  },
  [LOAD_IMPORTS_SUCCESS](state: SelfDataState, data: any) {
    state.imports = {
      data: data.imports,
      count: data.count,
      loading: false,
      error: null,
    };
  },
  [LOAD_IMPORTS_FAILURE](state: SelfDataState, error: string) {
    state.imports = { ...state.imports, loading: false, error: error };
  },

  [EDIT_IMPORTS](state: SelfDataState) {
    state.imports = { ...state.imports, loading: true, error: null };
  },
  [EDIT_IMPORTS_SUCCESS](state: SelfDataState, importFile: any) {
    state.imports.loading = false;
    state.imports.error = null;
    state.imports.data[
      state.imports.data.findIndex((cnt: any) => cnt.id === importFile.id)
    ] = { ...importFile };
  },
  [EDIT_IMPORTS_FAILURE](state: SelfDataState, error: string) {
    state.imports = { ...state.imports, loading: false, error: error };
  },

  [DELETE_IMPORT](state: SelfDataState) {
    state.imports = {
      ...state.imports,
      loading: true,
      error: null,
    };
  },
  [DELETE_IMPORT_SUCCESS](state: SelfDataState, importId: string) {
    state.imports = {
      data: state.imports.data.filter((file: any) => file.id !== importId),
      count: state.imports.count - 1,
      loading: false,
      error: null,
    };
  },
  [DELETE_IMPORT_FAILURE](state: SelfDataState, error: string) {
    state.imports = {
      ...state.imports,
      loading: false,
      error: error,
    };
  },
  // DOWNLOAD_CONFIGS
  [LOAD_DOWNLOAD_CONFIGS](state: SelfDataState) {
    state.downloadConfigs = {
      ...state.downloadConfigs,
      loading: true,
      error: null,
    };
  },
  [LOAD_DOWNLOAD_CONFIGS_SUCCESS](
    state: SelfDataState,
    downloadConfigs: DownloadConfig[],
  ) {
    state.downloadConfigs = {
      data: downloadConfigs,
      loading: false,
      error: null,
    };
  },
  [LOAD_DOWNLOAD_CONFIGS_FAILURE](state: SelfDataState, error: string) {
    state.downloadConfigs = {
      ...state.downloadConfigs,
      loading: false,
      error: error,
    };
  },
  [SEND_DOWNLOAD_CONFIGS](state: SelfDataState) {
    state.downloadConfigs = {
      ...state.downloadConfigs,
      loading: true,
      error: null,
    };
  },
  [SEND_DOWNLOAD_CONFIGS_SUCCESS](
    state: SelfDataState,
    addedConfig: DownloadConfig,
  ) {
    state.downloadConfigs = {
      data: [...state.downloadConfigs.data, addedConfig],
      loading: false,
      error: null,
    };
  },
  [SEND_DOWNLOAD_CONFIGS_FAILURE](state: SelfDataState, error: string) {
    state.downloadConfigs = {
      ...state.downloadConfigs,
      loading: false,
      error: error,
    };
  },
  [UPDATE_DOWNLOAD_CONFIGS](state: SelfDataState) {
    state.downloadConfigs = {
      ...state.downloadConfigs,
      loading: true,
      error: null,
    };
  },
  [UPDATE_DOWNLOAD_CONFIGS_SUCCESS](
    state: SelfDataState,
    updatedConfig: DownloadConfig,
  ) {
    state.downloadConfigs.data[
      state.downloadConfigs.data.findIndex(
        (conf: DownloadConfig) => conf.id === updatedConfig.id,
      )
    ] = updatedConfig;
    state.downloadConfigs.loading = false;
    state.downloadConfigs.error = null;
  },
  [UPDATED_DOWNLOAD_CONFIGS_FAILURE](state: SelfDataState, error: string) {
    state.downloadConfigs = {
      ...state.downloadConfigs,
      loading: false,
      error: error,
    };
  },
  [DELETE_DOWNLOAD_CONFIGS](state: SelfDataState) {
    state.downloadConfigs = {
      ...state.downloadConfigs,
      loading: true,
      error: null,
    };
  },
  [DELETE_DOWNLOAD_CONFIGS_SUCCESS](
    state: SelfDataState,
    downloadConfigId: string,
  ) {
    state.downloadConfigs = {
      data: state.downloadConfigs.data.filter(
        (conf: DownloadConfig) => conf.id !== downloadConfigId,
      ),
      loading: false,
      error: null,
    };
  },
  [DELETE_DOWNLOAD_CONFIGS_FAILURE](state: SelfDataState, error: string) {
    state.downloadConfigs = {
      ...state.downloadConfigs,
      loading: false,
      error: error,
    };
  },

  // DOWNLOAD_REQUEST
  [SEND_DOWNLOAD_REQUEST](state: SelfDataState) {
    state.downloadRequest = {
      loading: true,
      error: null,
    };
  },
  [SEND_DOWNLOAD_REQUEST_SUCCESS](state: SelfDataState) {
    state.downloadRequest = { loading: false, error: null };
  },
  [SEND_DOWNLOAD_REQUEST_FAILURE](state: SelfDataState, error: string) {
    state.downloadRequest = {
      loading: false,
      error: error,
    };
  },
  // FILES
  [LOAD_FILES](state: SelfDataState) {
    state.files = { ...state.files, loading: true, error: null };
  },
  [LOAD_FILES_SUCCESS](state: SelfDataState, files: DownloadRequest[]) {
    state.files = { data: files, loading: false, error: null };
  },
  [LOAD_FILES_FAILURE](state: SelfDataState, error: string) {
    state.files = { ...state.files, loading: false, error: error };
  },
  [DELETE_FILES](state: SelfDataState) {
    state.files = { ...state.files, loading: true, error: null };
  },
  [DELETE_FILES_SUCCESS](state: SelfDataState, deletedFile: DownloadRequest) {
    state.files = {
      data: state.files.data.filter(
        (file: DownloadRequest) => file.id !== deletedFile.id,
      ),
      loading: false,
      error: null,
    };
  },
  [DELETE_FILES_FAILURE](state: SelfDataState, error: string) {
    state.files = { ...state.files, loading: false, error: error };
  },
  [CLEAR_DOWNLOAD_CONFIG_SELF_DATE_STORE](state: SelfDataState) {
    state.downloadConfigs = { data: [], loading: false, error: null };
  },
  [CLEAR_FILES_SELF_DATA_STORE](state: SelfDataState) {
    state.files = { data: [], loading: false, error: null };
  },

  [SAVE_IMPORT_FILE](state: SelfDataState, file: any) {
    state.currentFileImport = file;
  },
  [CLEAR_IMPORT_FILE](state: SelfDataState) {
    state.currentFileImport = {};
  },

  // History VARIABLES Change
  [LOAD_HISTORY_VAR_CHANGE](state: SelfDataState) {
    state.historyVariableChange = {
      ...state.historyVariableChange,
      loading: true,
      error: null,
    };
  },
  [LOAD_LOAD_HISTORY_VAR_CHANGE_SUCCESS](state: SelfDataState, data: any) {
    state.historyVariableChange = {
      data: data.data,
      count: data.total_count,
      loading: false,
      error: null,
    };
  },

  [LOAD_LOAD_HISTORY_VAR_CHANGE_FAILURE](state: SelfDataState, error: string) {
    state.historyVariableChange = {
      ...state.historyVariableChange,
      loading: false,
      error: error,
    };
  },
};
